import { useEffect, useRef, useState } from 'react'
import { api, type Approval, type Schedule, type Task } from '../api'
import type { Anchor } from '../anchor'
import type { TasksState } from '../useTasks'
import { Close } from './icons'

/**
 * Фоновые задачи — заглянуть, а не разложить по полочкам.
 *
 * Раньше это была выдвижная панель во весь экран на телефоне и в половину на
 * десктопе, свёрстанная карточками: на три задачи уходил весь чат. Задача
 * описывается одной строкой — что делаем и сколько прошло, — поэтому список
 * стал списком, а подробности и кнопки открываются у той строки, которую
 * тронули.
 *
 * Одно исключение, и оно принципиальное: запрос разрешения показывается
 * целиком. §12 — одобрить пересказ команды не значит одобрить команду, так
 * что аргументы не сокращаются и не прячутся за прокруткой-обманкой.
 *
 * Данные приходят снаружи (`useTasks`): их же читает кнопка, которая эту
 * панель открывает, и два опроса на одно состояние разошлись бы.
 */

const STATE_LABEL: Record<Task['state'], string> = {
  proposed: 'ждёт подтверждения',
  queued: 'в очереди',
  running: 'в работе',
  waiting_approval: 'ждёт разрешения',
  paused: 'на паузе',
  done: 'готово',
  failed: 'не вышло',
  cancelled: 'снято',
}

/**
 * Состояние одной строкой, без цвета.
 *
 * В палитре нет ни одного оттенка, поэтому состояние читается формой и
 * движением, как у плашек инструментов в ответе: пульсирует — идёт, контур —
 * ждёт своей очереди, залитая точка — закончилось, пустая — не вышло.
 */
function Dot({ state }: { state: Task['state'] }) {
  const shape =
    state === 'running'
      ? 'animate-pulse bg-(--ink)'
      : state === 'queued' || state === 'proposed' || state === 'waiting_approval'
        ? 'border border-(--ink-dim)'
        : state === 'done'
          ? 'bg-(--ink-faint)'
          : 'border border-(--ink-faint)'
  return <span className={`mt-1.5 size-1.5 shrink-0 rounded-full ${shape}`} />
}

/** Мелкая кнопка действия. Их всегда две-три, и они всегда в одну строку. */
function Action({
  onClick,
  disabled,
  strong,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  /** Основное действие строки — заливкой, остальные контуром. */
  strong?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg px-2.5 py-1 text-xs transition disabled:opacity-40 ${
        strong
          ? 'bg-(--inverse-bg) text-(--inverse) hover:opacity-80'
          : 'border border-(--hairline) text-(--ink-dim) hover:bg-(--raised) hover:text-(--ink)'
      }`}
    >
      {children}
    </button>
  )
}

function TaskRow({
  task,
  busy,
  act,
}: {
  task: Task
  busy: boolean
  act: TasksState['act']
}) {
  const [open, setOpen] = useState(false)

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-(--raised)"
      >
        <Dot state={task.state} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-(--ink)">{task.goal}</span>
          {/* Свёрнутая строка говорит только то, что меняется: состояние и
              сколько шагов прошло. Всё остальное — по нажатию. */}
          <span className="block truncate text-xs text-(--ink-faint)">
            {STATE_LABEL[task.state]}
            {task.state === 'running' && ` · ${task.used_steps}/${task.budget_steps}`}
          </span>
        </span>
      </button>

      {open && (
        <div className="mb-1 space-y-1.5 pl-6 pr-2">
          <p className="text-xs text-(--ink-dim)">
            шагов {task.used_steps}/{task.budget_steps} · токенов{' '}
            {task.used_tokens}/{task.budget_tokens}
            {task.starts_at ? ` · запуск ${task.starts_at}` : ''}
            {task.schedule_id ? ' · по расписанию' : ''}
          </p>
          {task.error && (
            // Без красного: у палитры нет оттенков. Отбивка слева и
            // приглушённый текст — то же решение, что и в ответе с ошибкой.
            <p className="border-l-2 border-(--ink-faint) pl-2 text-xs text-(--ink-dim)">
              {task.error}
            </p>
          )}
          <div className="flex gap-1.5">
            {task.state === 'proposed' && (
              <Action strong disabled={busy} onClick={() => act(() => api.confirmTask(task.id))}>
                подтвердить
              </Action>
            )}
            <Action disabled={busy} onClick={() => act(() => api.cancelTask(task.id))}>
              {task.state === 'proposed' ? 'отклонить' : 'снять'}
            </Action>
          </div>
        </div>
      )}
    </li>
  )
}

function ScheduleRow({
  schedule,
  busy,
  act,
}: {
  schedule: Schedule
  busy: boolean
  act: TasksState['act']
}) {
  const [open, setOpen] = useState(false)

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-(--raised)"
      >
        <span
          className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
            schedule.state === 'active' ? 'bg-(--ink-faint)' : 'border border-(--ink-dim)'
          }`}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-(--ink)">{schedule.goal}</span>
          <span className="block truncate text-xs text-(--ink-faint)">
            раз в {schedule.every_hours} ч ·{' '}
            {schedule.state === 'proposed' ? 'ждёт подтверждения' : 'включено'}
          </span>
        </span>
      </button>

      {open && (
        <div className="mb-1 space-y-1.5 pl-6 pr-2">
          <p className="text-xs text-(--ink-dim)">следующий раз {schedule.next_run_at}</p>
          <div className="flex gap-1.5">
            {schedule.state === 'proposed' && (
              <Action
                strong
                disabled={busy}
                onClick={() => act(() => api.confirmSchedule(schedule.id))}
              >
                включить
              </Action>
            )}
            <Action disabled={busy} onClick={() => act(() => api.cancelSchedule(schedule.id))}>
              {schedule.state === 'proposed' ? 'отклонить' : 'выключить'}
            </Action>
          </div>
        </div>
      )}
    </li>
  )
}

/**
 * Запрос разрешения. Единственное, что здесь не сворачивается.
 *
 * Аргументы показываются целиком и переносом по символам: §12 — одобрить
 * пересказ команды не значит одобрить команду, а строка, уехавшая за правый
 * край, это и есть пересказ.
 */
function ApprovalCard({
  approval,
  busy,
  act,
}: {
  approval: Approval
  busy: boolean
  act: TasksState['act']
}) {
  return (
    <section className="mb-2 rounded-xl border-l-2 border-(--ink) bg-(--raised-hi) p-2.5">
      <p className="text-sm text-(--ink)">
        задача {approval.task_id} просит разрешения
      </p>
      <p className="mt-0.5 text-xs text-(--ink-dim)">
        инструмент <span className="font-mono text-(--ink)">{approval.tool}</span>
      </p>
      <pre className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-(--ground) p-2 font-mono text-[0.7rem] leading-5 text-(--ink)">
        {approval.args}
      </pre>
      <div className="mt-2 flex gap-1.5">
        <Action strong disabled={busy} onClick={() => act(() => api.decideApproval(approval.id, true))}>
          разрешить
        </Action>
        <Action disabled={busy} onClick={() => act(() => api.decideApproval(approval.id, false))}>
          отказать
        </Action>
      </div>
    </section>
  )
}

interface Props {
  open: boolean
  anchor: Anchor
  onClose: () => void
  state: TasksState
}

export function Tasks({ open, anchor, onClose, state }: Props) {
  const { tasks, schedules, approval, error, busy, act } = state
  const panel = useRef<HTMLDivElement>(null)

  // Всплывающая панель — временная поверхность: нажатие мимо или Escape её
  // убирает. Кнопку исключать не нужно: она сама переключает `open`, а
  // mousedown приходит раньше click и закрыл бы панель до её обработчика,
  // поэтому проверяем, что нажали не по ней.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (panel.current?.contains(t)) return
      if ((t as Element).closest?.('[data-tasks-toggle]')) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  const { origin, ...position } = anchor
  const empty = tasks.length === 0 && schedules.length === 0

  return (
    <div
      ref={panel}
      style={{ ...position, ['--peek-origin' as string]: origin }}
      className="peek fixed z-40 flex max-h-[60vh] w-[min(22rem,calc(100vw-1rem))] flex-col
                 overflow-hidden rounded-2xl border border-(--hairline) bg-(--surface) shadow-xl"
      aria-label="Фоновые задачи"
    >
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
        <p className="section-label">задачи</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="закрыть"
          title="закрыть"
          className="rounded-md p-1 text-(--ink-faint) transition hover:bg-(--raised-hi) hover:text-(--ink)"
        >
          <Close size={14} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-2">
        {error && (
          <p className="mb-2 border-l-2 border-(--ink-faint) px-2 py-1 text-xs text-(--ink-dim)">
            {error}
          </p>
        )}

        {approval && <ApprovalCard approval={approval} busy={busy} act={act} />}

        {empty && !approval && (
          <p className="px-2 py-3 text-xs text-(--ink-faint)">живых задач нет</p>
        )}

        {tasks.length > 0 && (
          <ul className="space-y-px">
            {tasks.map((t) => (
              <TaskRow key={t.id} task={t} busy={busy} act={act} />
            ))}
          </ul>
        )}

        {schedules.length > 0 && (
          <>
            <p className="section-label px-2 pt-3 pb-1">расписания</p>
            <ul className="space-y-px">
              {schedules.map((s) => (
                <ScheduleRow key={s.id} schedule={s} busy={busy} act={act} />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
